import { atom } from "jotai"
import { z } from "zod"
import {
	addressSchema,
	AddressType,
	defaultAddress,
	hqAddressDraftAtom,
} from "./draft/hqAddress"
import { defaultMileage, mileageDraftAtom, mileageSchema } from "./draft/mileage"
import { defaultPeriod, periodFormSchema, periodDraftAtom } from "./draft/period"
import { defaultVehicle, vehicleDraftAtom, vehicleSchema } from "./draft/vehicle"
import {
	defaultWorkingSchedule,
	workingScheduleDraftAtom,
	workingScheduleSchema,
} from "./draft/workingSchedule"
import { defaultTimeSpent, timeSpentDraftAtom, timeSpentSchema } from "./draft/timeSpent"
import { addressesDraftAtom, defaultAddresses } from "./draft/addresses"
import { atomWithReset } from "jotai/utils"
import { defaultProfession, professionDraftAtom } from "./draft/profession"
import { professionId } from "@/constants/professions"

export const reportSaveDataSchema = z.object({
	pdf_url: z.string().nullable().optional(),
	trips_url: z.string().nullable().optional(),
	form_url: z.string().nullable().optional(),
})
export const reportSaveSchema = z
	.object({
		paid: z.boolean(),
		terms_accepted: z.boolean(),
	})
	.merge(reportSaveDataSchema)

export type ReportSave = z.infer<typeof reportSaveSchema>
export type ReportSaveData = z.infer<typeof reportSaveDataSchema>

export const reportFormSchema = z.object({
	period: periodFormSchema,
	hqAddress: addressSchema,
	vehicle: vehicleSchema.optional(),
	mileage: mileageSchema,
	workingSchedule: workingScheduleSchema,
	timeSpent: timeSpentSchema,
	clients: z.array(addressSchema),
	professionType: professionId,
})
export type ReportFormData = z.infer<typeof reportFormSchema>

export const reportFormDraftAtom = atom<ReportFormData, [ReportFormData], void>(
	(get) => ({
		vehicle: get(vehicleDraftAtom),
		mileage: get(mileageDraftAtom),
		period: get(periodDraftAtom),
		hqAddress: get(hqAddressDraftAtom),
		workingSchedule: get(workingScheduleDraftAtom),
		timeSpent: get(timeSpentDraftAtom),
		clients: get(addressesDraftAtom),
		professionType: get(professionDraftAtom),
	}),
	(get, set, newValue) => {
		set(vehicleDraftAtom, newValue.vehicle)
		set(mileageDraftAtom, newValue.mileage)
		set(periodDraftAtom, newValue.period)
		set(hqAddressDraftAtom, newValue.hqAddress)
		set(workingScheduleDraftAtom, newValue.workingSchedule)
		set(timeSpentDraftAtom, newValue.timeSpent)
		set(addressesDraftAtom, newValue.clients || defaultAddresses)
		set(professionDraftAtom, newValue.professionType)
	}
)

export const defaultReportForm: ReportFormData = {
	period: defaultPeriod,
	hqAddress: defaultAddress,
	vehicle: defaultVehicle,
	mileage: defaultMileage,
	workingSchedule: defaultWorkingSchedule,
	timeSpent: defaultTimeSpent,
	clients: defaultAddresses,
	professionType: defaultProfession,
}
